import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Logo from '../components/styleguide/Logo';
import apiService from '../services/apiService';

/**
 * Página de verificación de email
 *
 * Permite ingresar código de 6 dígitos enviado al email del usuario.
 * Soporta entrada dígito a dígito, pegado de código completo, y reenvío.
 *
 * @returns {JSX.Element}
 */
function VerificarEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const emailFromState = location.state?.email;
  const emailFromParams = searchParams.get('email');
  const errorFromParams = searchParams.get('error');
  const successFromParams = searchParams.get('success');

  const email = emailFromState || emailFromParams || '';
  const [codigo, setCodigo] = useState(['', '', '', '', '', '']);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [message, setMessage] = useState(location.state?.mensaje || '');
  const [countdown, setCountdown] = useState(0);

  const inputRefs = useRef([]);

  useEffect(() => {
    if (successFromParams === 'true') {
      setMessage('Email verificado correctamente. Ya puedes iniciar sesion.');
      setTimeout(() => navigate('/login'), 3000);
    } else if (errorFromParams) {
      setErrors({ general: decodeURIComponent(errorFromParams) });
    }
  }, [successFromParams, errorFromParams, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  /**
   * Maneja cambio en un dígito del código
   * @param {number} index - Índice del dígito (0-5)
   * @param {string} value - Valor ingresado
   */
  const handleCodigoChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newCodigo = [...codigo];
    newCodigo[index] = value;
    setCodigo(newCodigo);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (errors.codigo) {
      setErrors(prev => ({ ...prev, codigo: '' }));
    }
  };

  /**
   * Maneja navegación con teclas (Backspace para ir al anterior)
   * @param {number} index - Índice del dígito actual
   * @param {KeyboardEvent} e - Evento de teclado
   */
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codigo[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  /**
   * Maneja pegado de código completo desde portapapeles
   * @param {ClipboardEvent} e - Evento de pegado
   */
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      setCodigo(pastedData.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  /**
   * Maneja envío del formulario de verificación
   * @param {Event} e - Evento del formulario
   * @returns {Promise<void>}
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const codigoCompleto = codigo.join('');

    if (!email) {
      setErrors({ general: 'Email no especificado' });
      return;
    }

    if (codigoCompleto.length !== 6) {
      setErrors({ codigo: 'Ingresa el codigo completo' });
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const data = await apiService.verificarCodigo(email, codigoCompleto);

      if (data.success) {
        setMessage('Email verificado correctamente!');
        setTimeout(() => navigate('/login'), 2000);
      } else {
        setIsLoading(false);
        setErrors({ general: data.message || 'Codigo incorrecto' });
        setCodigo(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      setIsLoading(false);
      setErrors({ general: error.message || 'Error verificando codigo' });
      setCodigo(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };

  /**
   * Solicita reenvío del código de verificación
   * @returns {Promise<void>}
   */
  const handleResendCode = async () => {
    if (!email || countdown > 0) return;

    setIsResending(true);
    setErrors({});

    try {
      const data = await apiService.reenviarCodigo(email);

      if (data.success) {
        setMessage('Nuevo codigo enviado a tu email');
        setCountdown(60);
        setCodigo(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } else {
        setErrors({ general: data.message || 'Error reenviando codigo' });
      }
    } catch (error) {
      setErrors({ general: error.message || 'Error reenviando codigo' });
    } finally {
      setIsResending(false);
    }
  };

  if (successFromParams === 'true') {
    return (
      <div className="login-container">
        <div className="login-card text-center">
          <div className="verification-success-icon">
            <i className="fas fa-check-circle"></i>
          </div>
          <h2 className="text-secondary margin-bottom-md">Email verificado</h2>
          <p className="text-muted">Redirigiendo al login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <Logo size="large" />
          <h2 className="login-title">Verificar email</h2>
          <p className="login-subtitle">Ingresa el codigo de 6 digitos enviado a</p>
          <p className="text-primary font-semibold margin-top-sm">
            {email || 'tu correo'}
          </p>
        </div>

        {message && !errors.general && (
          <div className="alert alert-success-light">
            <i className="fas fa-check-circle"></i>
            <span>{message}</span>
          </div>
        )}

        {errors.general && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle"></i>
            <span>{errors.general}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label className="form-label text-center">
              Codigo de verificacion
            </label>
            <div className="verification-code-container">
              {codigo.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleCodigoChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  disabled={isLoading}
                  className={`form-control verification-code-input ${errors.codigo ? 'is-invalid' : ''}`}
                />
              ))}
            </div>
            {errors.codigo && (
              <div className="invalid-feedback text-center margin-top-sm">
                <i className="fas fa-exclamation-circle"></i> {errors.codigo}
              </div>
            )}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-block btn-lg"
            disabled={isLoading || codigo.join('').length !== 6}
          >
            {isLoading ? (
              <><i className="fas fa-spinner fa-spin"></i> Verificando...</>
            ) : (
              <><i className="fas fa-check"></i> Verificar</>
            )}
          </button>
        </form>

        <div className="text-center" style={{ marginTop: 'var(--space-xl)' }}>
          <p className="text-muted margin-bottom-sm text-sm">
            No recibiste el codigo?
          </p>
          <button
            type="button"
            onClick={handleResendCode}
            disabled={isResending || countdown > 0}
            className={`login-link${countdown > 0 ? 'cursor-default text-muted' : 'cursor-pointer text-primary'}`}
          >
            {isResending ? (
              <><i className="fas fa-spinner fa-spin"></i> Enviando...</>
            ) : countdown > 0 ? (
              <><i className="fas fa-clock"></i> Reenviar en {countdown}s</>
            ) : (
              <><i className="fas fa-redo"></i> Reenviar codigo</>
            )}
          </button>
        </div>

        <div className="login-footer">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="login-linkcursor-pointer"
          >
            <i className="fas fa-arrow-left"></i> Volver al login
          </button>
        </div>
      </div>
    </div>
  );
}

export default VerificarEmail;
